/**
 * What "Save credentials" actually sends — V9.1 §1.
 *
 * Pulled out of the card as a pure function for one reason: this is where the
 * founder's outage lived, and a decision that expensive should be testable
 * without a browser. Given the edits a person made and what the server says is
 * already stored, it returns the exact request body — nothing else in the
 * component decides anything about credentials.
 *
 * ## The rule
 *
 * A secret field is in exactly one of three states, and each has its own wire
 * form, so the server never has to infer intent from a value's shape:
 *
 *   - **untouched** → `KEEP_EXISTING_SECRET`. The only thing that keeps a
 *     stored secret.
 *   - **typed** → the trimmed value. Always overwrites, whatever is stored.
 *   - **emptied on purpose** → `null`. Clears it.
 *
 * What is deliberately impossible: sending `''`. That was the old wire form for
 * "clear", it is also what a form emits by accident, and the server used to
 * honour it — deleting a stored secret access key while writing the new access
 * key id sent beside it. The API now rejects `''` outright; this function never
 * produces one.
 */

import { KEEP_EXISTING_SECRET } from '@virtuetasks/types';

/** Every write-only field this card can save, by its edit key. */
export const SECRET_PATHS = [
  'brevo.apiKey',
  'whatsapp.accessToken',
  's3.accessKeyId',
  's3.secretAccessKey',
] as const;

export type SecretPath = (typeof SECRET_PATHS)[number];

/** Plain (non-secret) fields, which keep ordinary omit-means-unchanged rules. */
const PLAIN_PATHS = [
  'brevo.senderEmail',
  'brevo.senderName',
  'whatsapp.phoneNumberId',
  's3.bucket',
  's3.region',
  's3.endpoint',
] as const;

export interface SavePayload {
  brevo: Record<string, string | null>;
  whatsapp: Record<string, string | null>;
  s3: Record<string, string | null>;
}

/**
 * One write-only field's wire value, from what was typed and what is stored.
 *
 * `undefined` means "leave this out of the request entirely" — used when there
 * is nothing stored and nothing typed, where any of the three instructions
 * would be noise.
 *
 * Exported because the calendar card saves through the same endpoint and must
 * obey the same contract; two implementations of this rule is how it drifts.
 */
export function writeOnlySecretValue(
  typed: string | undefined,
  stored: boolean,
): string | null | undefined {
  if (typed === undefined) return stored ? KEEP_EXISTING_SECRET : undefined;

  /**
   * Trimmed here as well as on the server.
   *
   * The server's trim is the one that counts — it is the boundary — but
   * trimming here means the value that goes on the wire is the value that gets
   * stored, so a key pasted out of the AWS console with a trailing newline
   * cannot produce a `SignatureDoesNotMatch` that looks like a wrong key.
   */
  const value = typed.trim();
  if (value === '') return stored ? null : undefined;
  return value;
}

/**
 * @param edits   what the operator typed, keyed by field path. A key is present
 *                only once its input has fired a change.
 * @param stored  whether the server reports a secret already held, by path.
 *                Drives nothing but the untouched case — a typed value wins
 *                regardless.
 */
export function buildSavePayload(
  edits: Readonly<Record<string, string>>,
  stored: Readonly<Record<string, boolean>>,
): SavePayload {
  const payload: SavePayload = { brevo: {}, whatsapp: {}, s3: {} };

  const put = (path: string, value: string | null): void => {
    const [group, field] = path.split('.');
    payload[group as keyof SavePayload][field] = value;
  };

  for (const path of SECRET_PATHS) {
    // Untouched with something stored → the sentinel, explicitly. Said out
    // loud rather than by omission, so a field that goes missing for any
    // reason can never again be read as "clear this".
    const value = writeOnlySecretValue(edits[path], Boolean(stored[path]));
    if (value !== undefined) put(path, value);
  }

  for (const path of PLAIN_PATHS) {
    if (edits[path] !== undefined) put(path, edits[path]);
  }

  return payload;
}
